import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/UserAvatar";
import {
  LayoutDashboard, BookOpen, Award, Star, CalendarClock, FolderOpen, Users,
  CreditCard, LogOut, Menu, ShieldCheck, ClipboardList, HelpCircle, Lock, ChevronUp, User, ExternalLink,
} from "lucide-react";

const NAV = {
  manager: [
    { to: "/app", label: "Compliance", icon: LayoutDashboard, end: true },
    { to: "/app/courses", label: "Courses", icon: BookOpen },
    { to: "/app/team", label: "My team", icon: Users },
    { to: "/app/training-report", label: "Training report", icon: ClipboardList },
    { to: "/app/oral-champion", label: "Oral Champion", icon: Star },
    { to: "/app/resources", label: "Resources", icon: FolderOpen },
    { to: "/app/faq", label: "FAQ", icon: HelpCircle },
    { to: "/app/billing", label: "Billing", icon: CreditCard },
  ],
  staff: [
    { to: "/app", label: "My learning", icon: LayoutDashboard, end: true },
    { to: "/app/courses", label: "Courses", icon: BookOpen },
    { to: "/app/certificates", label: "Certificates", icon: Award },
    { to: "/app/resources", label: "Resources", icon: FolderOpen },
    { href: "https://www.facebook.com/groups/27941660172109456", label: "Join the oral health community", icon: Users },
    { to: "/app/faq", label: "FAQ", icon: HelpCircle },
  ],
  trainer: [
    { to: "/app", label: "My sessions", icon: CalendarClock, end: true },
    { to: "/app/certificates", label: "Certificates", icon: Award },
    { to: "/app/resources", label: "Resources", icon: FolderOpen },
    { to: "/app/faq", label: "FAQ", icon: HelpCircle },
  ],
  admin: [
    { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/app/admin", label: "Manage", icon: ShieldCheck },
    { to: "/app/courses", label: "Courses", icon: BookOpen },
    { to: "/app/oral-champion", label: "Oral Champion", icon: Star },
    { to: "/app/resources", label: "Resources", icon: FolderOpen },
    { to: "/app/faq", label: "FAQ", icon: HelpCircle },
  ],
};

const ROLE_LABEL = { manager: "Care Home Manager", staff: "Care Staff", trainer: "Trainer", admin: "Platform Admin" };

export default function DashboardShell() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [home, setHome] = useState(null);

  useEffect(() => {
    const loadHome = () => { if (user?.care_home_id) api.get("/care-home/me").then((r) => setHome(r.data)).catch(() => {}); };
    loadHome();
    window.addEventListener("care-home-updated", loadHome);
    return () => window.removeEventListener("care-home-updated", loadHome);
  }, [user]);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (!user) return null;
  const items = NAV[user.role] || NAV.staff;
  const isActive = (item) => (item.end ? loc.pathname === item.to : loc.pathname.startsWith(item.to));

  const trialBanner = home && ["trial", "founding"].includes(home.subscription_status);
  const locked = !!home?.trial_expired && !loc.pathname.startsWith("/app/billing");

  const lockScreen = (
    <div data-testid="trial-lockout" className="max-w-xl mx-auto mt-10 rounded-3xl bg-cream-card border border-cream-deep p-8 md:p-10 text-center">
      <div className="h-14 w-14 rounded-2xl bg-coral/10 text-coral grid place-items-center mx-auto mb-5"><Lock className="h-7 w-7" /></div>
      <span className="label-caps text-coral">Free trial ended</span>
      <h2 className="text-2xl md:text-3xl font-bold text-brand mt-2">Subscribe to continue</h2>
      <p className="text-muted-foreground mt-3">
        {user.role === "manager"
          ? `Your 14-day trial of KOHC for ${home?.name || "your care home"} has ended. Choose a Care Home Licence to keep your team's training, certificates and compliance evidence.`
          : `Your care home's free trial of KOHC has ended. Ask your manager to subscribe so you can continue your training.`}
      </p>
      {user.role === "manager" && <Button data-testid="lockout-subscribe-btn" onClick={() => nav("/app/billing")} className="mt-6 h-11 px-8 rounded-full bg-coral hover:bg-coral-hover text-white">See plans & subscribe</Button>}
      <p className="text-xs text-muted-foreground mt-5">Your data is safe — everything is restored the moment your licence starts.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream font-body flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-40 w-64 bg-brand text-white flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 shrink-0 flex items-center gap-2.5 px-6 border-b border-white/10">
          <span className="h-9 w-9 rounded-lg bg-white text-brand grid place-items-center font-heading font-bold">K</span>
          <span className="font-heading font-bold text-lg">KOHC</span>
        </div>
        <nav className="flex-1 min-h-0 px-3 py-5 space-y-1 overflow-y-auto">
          {items.map((item) => item.href ? (
            <a key={item.href} href={item.href} target="_blank" rel="noreferrer" data-testid={`side-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-white/70 hover:bg-white/10 hover:text-white">
              <item.icon className="h-[18px] w-[18px]" />{item.label}<ExternalLink className="h-3 w-3 ml-auto opacity-60" />
            </a>
          ) : (
            <Link key={item.to} to={item.to} data-testid={`side-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item) ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <item.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />{item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-white/10 transition-colors">
                <UserAvatar name={user.name} url={user.photo_url} testId="sidebar-avatar" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
                  <div className="text-xs text-white/60 truncate">{ROLE_LABEL[user.role]}</div>
                </div>
                <ChevronUp className="h-4 w-4 text-white/60 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-56">
              <DropdownMenuItem data-testid="user-menu-profile" onSelect={() => { setOpen(false); nav("/app/profile"); }}>
                <User className="h-4 w-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onSelect={() => { logout(); nav("/"); }}>
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-cream-card border-b border-cream-deep flex items-center justify-between px-5 md:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-brand" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
            {home?.logo_url && <img data-testid="header-home-logo" src={home.logo_url} alt="" className="h-9 max-w-[120px] object-contain" />}
            {home && <span className="font-heading font-semibold text-brand truncate">{home.name}</span>}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/")} className="text-brand hidden sm:inline-flex">View site</Button>
          </div>
        </header>

        {trialBanner && !home.trial_expired && (
          <div data-testid="trial-banner" className="bg-coral/10 border-b border-coral/20 px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-brand font-medium">
              {home.subscription_status === "founding" ? "Founding member access" : "Free trial"} — expires {home.trial_ends_at ? fmtDate(home.trial_ends_at) : "soon"}. Subscribe to keep your compliance evidence.
            </span>
            {user.role === "manager" && <Button size="sm" data-testid="banner-upgrade-btn" onClick={() => nav("/app/billing")} className="rounded-full bg-coral hover:bg-coral-hover text-white">Subscribe now</Button>}
          </div>
        )}

        <main className="flex-1 p-5 md:p-8 max-w-[1400px] w-full mx-auto">{locked ? lockScreen : <Outlet />}</main>
        <footer className="px-5 md:px-8 py-4 text-center text-xs text-muted-foreground border-t border-cream-deep" data-testid="app-footer">© 2026 KOHC. All rights reserved.</footer>
      </div>
    </div>
  );
}
